// @ts-strict-ignore
import {DisplayOptions, ShowClause} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
} from 'common/types/reports/bookmark';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {properties} from 'common/report/util/settings-tracking';
// eslint-disable-next-line import/no-cycle
import {checkIsMultiMetricBookmark, getDisplayOptions} from 'common/report/insights/util';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';
import {ResultsKeysAndValues} from 'common/query-builder/query-report-header/types';

import {ANOMALY_DETECTION_ALLOWED_TIME_UNITS, ANY_VALUE, CUSTOM_ALERT_TYPE} from './constants';
import {CustomAlert, SlackWebhookError, SlackWebookSubscription, SubscriptionType} from './types';

export interface SavedAlertKey {
  header: string;
  value: string;
  displayHeader?: string;
  displayValue?: string;
}

// Selected alert key with required display fields for UI
export interface SelectedAlertKey {
  header: string;
  value: string | null;
  displayHeader: string;
  displayValue: string | null;
}

export interface MetricTypeChangeInfo {
  hasChange: boolean;
  oldType?: string;
  newType?: string;
}

// Raw API response type for custom alerts
export interface RawCustomAlertResponse {
  id: number;
  name: string;
  paused: boolean;
  frequency: number;
  valid?: boolean;
  condition: CustomAlert[`condition`];
  subscriptions: CustomAlert[`subscriptions`];
  bookmark: {
    id: number;
    name: string;
    type: string;
    params?: TODO;
    unit?: string;
  };
  creator: {
    id: number;
    name: string;
  };
  created: string;
  project: {
    id: number;
    name: string;
  };
  workspace?: {
    id: number;
  };
  notification_windows?: CustomAlert[`notificationWindows`];
}

/**
 * Transform raw API response to CustomAlert type.
 * The API returns nested objects (bookmark, creator, project, workspace) and `valid` field,
 * while our CustomAlert type uses flat structure with camelCase fields like bookmarkId, creatorName, isValid.
 */
export function transformRawAlertResponse(raw: RawCustomAlertResponse): CustomAlert {
  return {
    id: raw.id,
    name: raw.name,
    paused: raw.paused,
    frequency: raw.frequency,
    isValid: raw.valid,
    condition: raw.condition,
    subscriptions: raw.subscriptions,
    bookmarkId: raw.bookmark?.id,
    bookmarkName: raw.bookmark?.name,
    bookmarkType: raw.bookmark?.type,
    bookmarkParams: raw.bookmark?.params,
    bookmarkUnit: raw.bookmark?.unit,
    creatorId: raw.creator?.id,
    creatorName: raw.creator?.name,
    createdAt: raw.created,
    projectId: raw.project?.id,
    projectName: raw.project?.name,
    workspaceId: raw.workspace?.id,
    notificationWindows: raw.notification_windows,
  };
}

export function validateSlackWebhook(subscription: SlackWebookSubscription): SlackWebhookError {
  if (subscription.type !== SubscriptionType.SlackWebhook) {
    throw Error(`Incorrect subscription type`);
  }

  let error = null;
  if (!subscription.data.slack_webhook_url.startsWith(`https://hooks.slack.com/services/`)) {
    error = SlackWebhookError.InvalidUrl;
  }
  return error;
}

export function customAlertCoreTrackingProperties(customAlert: CustomAlert) {
  const {bookmarkId, id, projectId} = customAlert;
  return {[properties.projectId]: projectId, [properties.bookmarkId]: bookmarkId, [properties.customAlertId]: id};
}

export function customAlertTrackingProperties(customAlert: CustomAlert) {
  const {condition, subscriptions} = customAlert;
  const emailSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.Email);
  const webhookSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.SlackWebhook);
  const channelSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.SlackChannel);
  const projectWebhookSubscriptions = (subscriptions || []).filter(
    (sub) => sub.type === SubscriptionType.ProjectWebhook,
  );
  return {
    ...customAlertCoreTrackingProperties(customAlert),
    'Condition Threshold': condition.value,
    'Condition Operator': condition.op,
    'Condition Type': condition.type,
    '# of Email recipients': emailSubscriptions.length,
    '# of Recipients': subscriptions.length,
    '# of Webhook Recipients': webhookSubscriptions.length,
    '# of Slack Recipients': channelSubscriptions.length,
    '# of Project Webhook Recipients': projectWebhookSubscriptions.length,
    'Has Email Recipients': !!subscriptions.length,
    'Has Webhook Recipients': !!webhookSubscriptions.length,
    'Has Slack Recipients': !!channelSubscriptions.length,
    'Has Project Webhook Recipients': !!projectWebhookSubscriptions.length,
    ...(condition.type === CUSTOM_ALERT_TYPE.anomaly
      ? {
          Confidence: condition.confidence,
          'Training Mode': condition.trainingMode,
        }
      : {}),
  };
}

export function isAnomalyDetectionVizEnabled() {
  return true;
}

export function checkConversionWindowValidityForFunnelsMetrics(bookmarkParams: any, isMultiMetricBookmark: boolean) {
  if (!isMultiMetricBookmark) {
    return true;
  }

  const behaviorClauses = getBehaviorShowClauses(bookmarkParams.sections.show);
  const {type, conversionWindowUnit, conversionWindowDuration} = behaviorClauses[0][`behavior`];
  const isFunnel = type === `funnel`;

  // Conversion Window can't be longer than 3 months for anomaly detection
  const durationMap = new Map([
    [`quarter`, 1],
    [`month`, 3],
    [`week`, 12],
    [`day`, 90],
    [`hour`, 90 * 24],
    [`minute`, 90 * 24 * 60],
    [`second`, 90 * 24 * 60 * 60],
  ]);
  const isLongDuration = durationMap.get(conversionWindowUnit) <= conversionWindowDuration;

  let isValidConversionWindow = true;
  if (isMultiMetricBookmark && isFunnel && isLongDuration) {
    isValidConversionWindow = false;
  }
  return isValidConversionWindow;
}

// keep logic in sync with backend is_bookmark_valid_for_anomaly_detection_alert
// https://github.com/mixpanel/analytics/blob/ecc9465876c6f8d14735252d170108eeed84db4d/webapp/custom_alerts/util.py#L369
export function isValidAnomalyDetectionBookmark(
  bookmarkType: BookmarkType,
  bookmarkParams: {
    sections: any;
    displayOptions?: DisplayOptions;
    timeComparison?: any;
    liftComparison?: any;
  },
) {
  const isMultiMetricBookmark = checkIsMultiMetricBookmark(bookmarkParams?.sections?.show);

  if (bookmarkType === BookmarkType.Insights || isMultiMetricBookmark) {
    const displayOptions = bookmarkParams?.displayOptions;
    const bookmarkChartType = displayOptions?.chartType;
    const bookmarkPlotStyle = displayOptions?.plotStyle;
    const bookmarkAnalysisType = displayOptions?.analysis;
    const bookmarkHasTimeComparison = !!bookmarkParams?.timeComparison;
    const bookmarkHasLiftComparison = !!bookmarkParams?.liftComparison;
    const isValidConversionWindow = checkConversionWindowValidityForFunnelsMetrics(
      bookmarkParams,
      isMultiMetricBookmark,
    );

    const isCohortOvertime = bookmarkParams?.sections?.show?.some((showElement) => {
      const resourceType = isMultiMetricBookmark ? showElement.behavior?.resourceType : showElement.resourceType;
      return resourceType === InsightsResourceType.Cohorts;
    });

    const isAllowedTimeUnit = ANOMALY_DETECTION_ALLOWED_TIME_UNITS.includes(bookmarkParams.sections.time[0]?.unit);

    const isValid = !(
      ![ChartType.Line, ChartType.Column].includes(bookmarkChartType) ||
      bookmarkPlotStyle !== ChartPlotStyle.Standard ||
      bookmarkHasLiftComparison ||
      bookmarkHasTimeComparison ||
      !isValidConversionWindow ||
      bookmarkAnalysisType === AnalysisType.Rolling ||
      bookmarkAnalysisType === AnalysisType.Cumulative ||
      isCohortOvertime ||
      !isAllowedTimeUnit
    );
    return isValid;
  }
  return false;
}

export function isCustomAlertConditionTypesEnabled(bookmarkType: BookmarkType, bookmarkParams: any) {
  return (
    bookmarkType === BookmarkType.Insights &&
    [ChartType.Line, ChartType.Column].includes(
      getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions).chartType,
    )
  );
}

// Bookmark type required because isPercentage and isPercentageThreshold is currenlty only used for funnel alerts.
// With new multimetric bookmarks and count types across both funnels and insights, it could be possible to expand this
// to insights in the future but currently we are not touching insights when it comes to percentage alert types.
// Insights alert conditions are handled differently until we put some effort into a refactor to have the two alert types converge.
export function isPercentageAlertType(bookmarkType: BookmarkType, bookmarkParams: InsightsBookmarkParams) {
  const bookmarkSections = bookmarkParams?.sections;
  const isMultiMetricBookmark = checkIsMultiMetricBookmark(bookmarkSections?.show as ShowClause[]);
  const isFunnelsBookmark = bookmarkType === BookmarkType.Funnels;
  let isPercentage = false;
  if (isMultiMetricBookmark && isFunnelsBookmark) {
    const countType = bookmarkSections?.show[0][`measurement`][`math`];
    if (
      [
        FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      ].includes(countType)
    ) {
      isPercentage = true;
    }
  }
  return isPercentage;
}

/**
 * Check if a saved alert key is in sync with the current report's structure.
 * Both header and value must match the report key.
 */
export function isAlertKeyInSync(savedKey?: SavedAlertKey, reportKey?: ResultsKeysAndValues): boolean {
  if (!savedKey || !reportKey) {
    return false;
  }
  // Check header match
  if (savedKey.header !== reportKey.name) {
    return false;
  }
  // ANY_VALUE is a special synthetic value that's always valid when header matches
  if (savedKey.value === ANY_VALUE) {
    return true;
  }
  return reportKey.values?.includes(savedKey.value) ?? false;
}

/**
 * Detect if the metric type (first condition element) has changed between saved alert and current report.
 * @param savedAlertKeys - The saved alert condition keys
 * @param reportKeys - The current report keys
 * @returns Object with hasChange flag and old/new type names
 */
export function getMetricTypeChangeInfo(
  savedAlertKeys: SavedAlertKey[] | string[],
  reportKeys: ResultsKeysAndValues[],
  renamePropertyFn?: (header: string) => string,
): MetricTypeChangeInfo {
  const noChange = {hasChange: false, oldType: null, newType: null};

  if (!savedAlertKeys || savedAlertKeys.length === 0) {
    return noChange;
  }

  // Legacy format (array of strings) - can't determine metric type change
  if (typeof savedAlertKeys[0] === `string`) {
    return noChange;
  }

  if (!reportKeys || reportKeys.length === 0) {
    return noChange;
  }

  // The first element represents the metric type
  const savedMetricKey = savedAlertKeys[0] as SavedAlertKey;
  const currentMetricKey = reportKeys[0];

  if (savedMetricKey.header !== currentMetricKey.name) {
    const rename = renamePropertyFn || ((h: string) => h);
    return {
      hasChange: true,
      oldType: rename(savedMetricKey.header),
      newType: currentMetricKey.displayName || rename(currentMetricKey.name),
    };
  }

  return noChange;
}

/**
 * Get the names of conditions from the saved alert that no longer exist in the report.
 * Excludes the first element (metric type) - that's handled separately by getMetricTypeChangeInfo.
 * @param savedAlertKeys - The saved alert condition keys
 * @param reportKeys - The current report keys
 * @param renamePropertyFn - Function to convert property names to display names
 * @returns Array of removed condition display names
 */
export function getRemovedConditionNames(
  savedAlertKeys: SavedAlertKey[] | string[],
  reportKeys: ResultsKeysAndValues[],
  renamePropertyFn: (header: string) => string = (header) => header,
): string[] {
  if (!savedAlertKeys || savedAlertKeys.length === 0) {
    return [];
  }

  // Legacy format (array of strings) - can't determine removed conditions
  if (typeof savedAlertKeys[0] === `string`) {
    return [];
  }

  if (!reportKeys || reportKeys.length === 0) {
    // No report data available (e.g., editing from settings page) - can't determine removed conditions
    return [];
  }

  const reportKeyNames = new Set(reportKeys.map((rk) => rk.name));
  return (savedAlertKeys as SavedAlertKey[])
    .filter((savedKey, idx) => {
      // Skip the first element (metric type)
      if (idx === 0) {
        return false;
      }
      return !reportKeyNames.has(savedKey.header);
    })
    .map((savedKey) => renamePropertyFn(savedKey.header));
}

/**
 * Build selected alert keys based on saved alert keys and current report structure.
 * This ensures obsolete conditions (for removed breakdowns) are abandoned
 * and new breakdowns get a default value.
 * @param savedAlertKeys - The saved alert condition keys (may be legacy format)
 * @param reportKeys - The current report keys
 * @returns Array of selected alert keys for the UI
 */
export function buildSelectedAlertKeys(
  savedAlertKeys: Array<SavedAlertKey | string>,
  reportKeys: ResultsKeysAndValues[],
): SelectedAlertKey[] {
  if (!savedAlertKeys || savedAlertKeys.length === 0) {
    // No saved keys - use defaults from report
    return reportKeys.map((reportKey) => ({
      header: reportKey.name,
      displayHeader: reportKey.displayName || reportKey.name,
      value: reportKey.values && reportKey.values.length > 0 ? reportKey.values[0] : null,
      displayValue: reportKey.displayValues && reportKey.displayValues.length > 0 ? reportKey.displayValues[0] : null,
    }));
  }

  const isLegacyFormat = typeof savedAlertKeys[0] === `string`;
  const normalizedSavedKeys: SavedAlertKey[] = isLegacyFormat
    ? (savedAlertKeys as string[]).map((key) => ({header: ``, value: key}))
    : (savedAlertKeys as SavedAlertKey[]);

  // No report data available (e.g., editing from settings page)
  // Just use saved alert keys directly
  if (!reportKeys || reportKeys.length === 0) {
    return normalizedSavedKeys.map((key) => ({
      header: key.header,
      value: key.value,
      displayHeader: key.displayHeader || key.header,
      displayValue: key.displayValue || key.value,
    }));
  }

  // Build selectedAlertKeys based on current report structure
  return reportKeys.map((reportKey, idx) => {
    // Find matching saved key by header, or use position-based match for legacy format
    const savedKey =
      isLegacyFormat || normalizedSavedKeys.some((k) => !k.header)
        ? normalizedSavedKeys[idx] // Legacy: position-based
        : normalizedSavedKeys.find((k) => k.header === reportKey.name); // Modern: header-based

    if (savedKey) {
      const valueIndex = reportKey.values?.indexOf(savedKey.value) ?? -1;
      return {
        header: reportKey.name,
        value: savedKey.value,
        displayHeader: reportKey.displayName || reportKey.name,
        displayValue: valueIndex >= 0 ? reportKey.displayValues?.[valueIndex] ?? savedKey.value : savedKey.value,
      };
    }

    // No matching saved key - new breakdown, require user to select a value
    return {
      header: reportKey.name,
      value: null,
      displayHeader: reportKey.displayName || reportKey.name,
      displayValue: null,
    };
  });
}
